import React, { useEffect, useState } from 'react'
import ToastMessage from '../../../../common/ToastMessage';
import CustomDataGrid from '../../../../common/CustomDataGrid';
import useDepartment from '../../../../store/ReactQueries/departments/useDepartment';
import useAddDepartmen from '../../../../store/ReactQueries/departments/useAddDepartments';
import useUpdateDepartment from '../../../../store/ReactQueries/departments/useUpdateDepartment';
import useDeleteDepartment from '../../../../store/ReactQueries/departments/useDeleteDepartment';
import { GridColDef } from '@mui/x-data-grid';
import { PAGESIZE } from '../../../../utils/constants';
import { FieldErrors } from '../../../../domain/helpers/Errors';
import { formatDate } from '../../../../common/GlobalFunctions';
import { validateSchema } from '../../../../common/ValidateSchema';
import { useDarkModeStore } from '../../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../../store/useLanguageStore';
import { DepartmentInterface } from '../../../../models/TicketModule/Department';
import { departmentSchema } from '../../../../domain/ValidationSchemas/Ticket/department';

export const Departments = () => {
    const { language } = useLanguageStore();
    const { darkMode } = useDarkModeStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<DepartmentInterface>({
        id: '',
        title: '',
        isDefault: false,
    });

    const [modal, setModal] = useState<{ add: boolean, edit: boolean, view: boolean }>({
        add: false,
        edit: false,
        view: false
    });

    let { data: departments, error: departmentError, isLoading, refetch } = useDepartment(page, pageSize);

    const addDepartment = useAddDepartmen(page, pageSize);
    const updateDepartment = useUpdateDepartment(page, pageSize);
    const deleteDepartment = useDeleteDepartment(page, pageSize);


    useEffect(() => {
        if (departmentError) ToastMessage.Failed(`Error: ${departmentError.message}`);
    }, [departmentError])

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Id', type: "string", editable: false, width: 150 },
        { field: 'title', headerName: 'Title', type: "string", editable: true, width: 200 },
        {
            field: 'isDefault',
            headerName: 'IsDefault',
            type: 'boolean',
            editable: true,
            renderCell: ({ value }) => (
                <span style={{ color: darkMode ? 'white' : 'black' }}>
                    {value ? `✓` : '✕'}
                </span>
            ),
        },
        { field: 'createdDate', headerName: 'CreatedDate', type: "dateTime", editable: false, width: 150, valueFormatter: formatDate },
        { field: 'updatedDate', headerName: 'UpdatedDate', type: "dateTime", editable: false, width: 150, valueFormatter: formatDate },
        { field: 'tenantId', headerName: 'TenantId', type: "string", editable: false, width: 150 },
        { field: 'authorId', headerName: 'AuthorId', type: "string", editable: false, width: 150 },
        { field: 'modifiedById', headerName: 'ModifiedById', type: "string", editable: false, width: 150 },
    ];

    const addItems = () => {
        setForm({ ...form, title: '', id: '', isDefault: false });
        setModal({ ...modal, add: true });
        setErrors({});
    }
    const handleError = (error: unknown) => {
        if (error instanceof Error) {
            console.log(error.message);
            ToastMessage.Failed(error.message);
        } else {
            console.log("An unknown error occurred");
            ToastMessage.Failed();
        }
    };

    const handleSave = async () => {
        const isValid = validateSchema(departmentSchema, { title: form.title });

        setErrors(isValid.errors);
        if (!isValid.result) return;

        const { id, title, isDefault } = form;

        const upForm: DepartmentInterface = {
            id,
            title,
            isDefault
        };

        if (upForm.id === '') {
            const { id, ...formData } = upForm;
            addDepartment.mutate(formData, {
                onSuccess: () => {
                    if (upForm.isDefault) refetch();
                    ToastMessage.Success();
                },
                onError: (error) => handleError(error),
            });
        } else {
            updateDepartment.mutate(upForm, {
                onSuccess: () => {
                    if (upForm.isDefault) refetch();
                    ToastMessage.Success()
                },
                onError: (error) => handleError(error),
            });
        }
        setModal({ ...modal, add: false, edit: false, view: false });
    }

    const handleDelete = async (id: string) => {
        if (await ToastMessage.Confirm(language['ToastDeleteItem'])) {
            deleteDepartment.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
    };

    return (
        <CustomDataGrid
            name={language['Departments']}
            rows={departments || []}
            showColumnModal={['title', 'isDefault']}
            hideColumns={['authorId', 'id', 'tenantId', 'modifiedById']}
            form={form}
            page={page}
            modal={modal}
            errors={errors}
            columns={columns}
            loading={isLoading}
            pageSize={pageSize}
            setForm={setForm}
            setPage={setPage}
            addItems={addItems}
            setModal={setModal}
            setErrors={setErrors}
            handleSave={handleSave}
            setPageSize={setPageSize}
            handleDelete={handleDelete}
        />
    );
}
export default Departments;